import React, { useState, useEffect } from 'react';
import { useHistory, useParams, Link } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import SuperadminLayout from './SuperadminLayout';
import axios from '../../axios/axiosconf';
import { toInputDateValue } from './superadminDateUtils';
import './SuperadminStyles.css';

const parseApiErrors = (data) => {
    const next = {};
    if (!data) return next;

    if (data.errors && typeof data.errors === 'object') {
        Object.keys(data.errors).forEach((key) => {
            const val = data.errors[key];
            next[key] = Array.isArray(val) ? val[0] : val;
        });
    }

    if (data.message) {
        next.general = data.message;
    } else if (Object.keys(next).length > 0) {
        next.general = Object.values(next)[0];
    }

    return next;
};

const OPCIONES_PERIODICIDAD_RESET = [
    { value: 'mensual', label: 'Mensual' },
    { value: 'semestral', label: 'Semestral' },
    { value: 'anual', label: 'Anual' },
];

const SelectPeriodicidadReset = ({ id, value, onChange }) => (
    <select className="form-control" id={id} value={value} onChange={onChange}>
        {OPCIONES_PERIODICIDAD_RESET.map((o) => (
            <option key={o.value} value={o.value}>
                {o.label}
            </option>
        ))}
    </select>
);

const defaultServicios = () => ({
    fev: { activo: false, periodicidad: 'mensual', fecha_inicio: '' },
    sms: { activo: false, limite: '', periodicidad: 'mensual', fecha_inicio: '' },
    whatsapp: { activo: false, limite: '', periodicidad: 'mensual', fecha_inicio: '' },
    consentimientos: { activo: false, limite: '', periodicidad: 'mensual', fecha_inicio: '' },
});

const mapServiciosFromApi = (apiServicios) => {
    const base = defaultServicios();
    if (!apiServicios || typeof apiServicios !== 'object') {
        return base;
    }

    ['fev', 'sms', 'whatsapp', 'consentimientos'].forEach((key) => {
        const row = apiServicios[key];
        if (!row) return;
        base[key] = {
            ...base[key],
            activo: !!row.activo,
            periodicidad: row.periodicidad || base[key].periodicidad,
            fecha_inicio: toInputDateValue(row.fecha_inicio),
            ultimo_reset: toInputDateValue(row.ultimo_reset),
            ...(key !== 'fev' && row.limite != null ? { limite: String(row.limite) } : {}),
            ...(row.consumo_actual != null ? { consumo_actual: row.consumo_actual } : {}),
        };
    });

    return base;
};

const ServicioFechaInicio = ({ id, value, onChange, ultimoReset }) => (
    <div className="form-group mb-0">
        <label htmlFor={id}>Fecha de recarga</label>
        <input type="date" className="form-control" id={id} value={value} onChange={onChange} />
        <small className="form-text text-muted">
            Próxima recarga programada. Resetear periodo solo aplica cuando hoy es igual o posterior a esta fecha.
            {ultimoReset && (
                <span className="d-block">
                    Última renovación: <strong>{ultimoReset}</strong>
                </span>
            )}
        </small>
    </div>
);

const normalizeColombianMobileInput = (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    if (digits.length === 12 && digits.startsWith('57')) {
        return digits.slice(2);
    }
    return digits.slice(0, 10);
};

const getEpaycoSubscriptionBadge = (sub) => {
    if (!sub?.epayco_subscription_id) {
        return { label: 'Sin inscribir', className: 'badge-secondary' };
    }
    if (sub.debito_automatico_pausado) {
        return { label: 'Débito pausado (clínica)', className: 'badge-secondary' };
    }
    const pendingFirst = !sub.fecha_primer_pago && sub.epayco_subscription_status === 'inactive';
    if (pendingFirst) {
        return { label: 'Inscrita (inactiva)', className: 'badge-warning' };
    }
    if (sub.epayco_subscription_status === 'active' || sub.has_active_subscription) {
        return { label: 'Activa', className: 'badge-success' };
    }
    return { label: 'Inscrita', className: 'badge-info' };
};

const PasswordInput = ({
    id,
    name,
    value,
    onChange,
    disabled,
    placeholder,
    autoComplete,
    className = 'form-control form-control-sm',
}) => {
    const [visible, setVisible] = useState(false);

    return (
        <div className="input-group input-group-sm sa-epayco-cvc-input">
            <input
                id={id}
                name={name}
                type={visible ? 'text' : 'password'}
                className={className}
                value={value}
                onChange={onChange}
                disabled={disabled}
                placeholder={placeholder}
                autoComplete={autoComplete}
            />
            <div className="input-group-append">
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setVisible((v) => !v)}
                    disabled={disabled}
                    aria-label={visible ? 'Ocultar CVC' : 'Mostrar CVC'}
                    tabIndex={-1}
                >
                    <i className={`fas fa-eye${visible ? '-slash' : ''}`} />
                </button>
            </div>
        </div>
    );
};

const EpaycoFieldLabel = ({ htmlFor, children }) => (
    <label className="small font-weight-bold mb-1 d-block text-secondary" htmlFor={htmlFor}>
        {children}
    </label>
);

const EpaycoPaymentCardFields = ({ prefix, form, onChange, disabled }) => (
    <>
        <div className="form-group mb-2">
            <EpaycoFieldLabel htmlFor={`${prefix}_card_number`}>Número de tarjeta</EpaycoFieldLabel>
            <div className="sa-epayco-card-wrap">
                <input
                    id={`${prefix}_card_number`}
                    name="card_number"
                    className="form-control form-control-sm"
                    placeholder="1234 1234 1234 1234"
                    autoComplete="cc-number"
                    value={form.card_number}
                    onChange={onChange}
                    required
                    disabled={disabled}
                />
                <div className="sa-epayco-card-brands" aria-hidden="true">
                    <span>VISA</span>
                    <span>MC</span>
                    <span>AMEX</span>
                </div>
            </div>
        </div>
        <div className="row">
            <div className="col-md-4 form-group mb-2">
                <EpaycoFieldLabel htmlFor={`${prefix}_card_exp_month`}>Mes (MM)</EpaycoFieldLabel>
                <input
                    id={`${prefix}_card_exp_month`}
                    name="card_exp_month"
                    className="form-control form-control-sm"
                    placeholder="MM"
                    maxLength={2}
                    value={form.card_exp_month}
                    onChange={onChange}
                    required
                    disabled={disabled}
                />
            </div>
            <div className="col-md-4 form-group mb-2">
                <EpaycoFieldLabel htmlFor={`${prefix}_card_exp_year`}>Año (AAAA)</EpaycoFieldLabel>
                <input
                    id={`${prefix}_card_exp_year`}
                    name="card_exp_year"
                    className="form-control form-control-sm"
                    placeholder="AAAA"
                    maxLength={4}
                    value={form.card_exp_year}
                    onChange={onChange}
                    required
                    disabled={disabled}
                />
            </div>
            <div className="col-md-4 form-group mb-2">
                <EpaycoFieldLabel htmlFor={`${prefix}_card_cvc`}>Código de seguridad (CVV)</EpaycoFieldLabel>
                <PasswordInput
                    id={`${prefix}_card_cvc`}
                    name="card_cvc"
                    value={form.card_cvc}
                    onChange={onChange}
                    disabled={disabled}
                    placeholder="123"
                    autoComplete="cc-csc"
                />
            </div>
        </div>
    </>
);

const EpaycoDocumentFields = ({ prefix, form, onChange, disabled, docTypes }) => (
    <div className="row">
        <div className="col-md-4 form-group mb-2">
            <EpaycoFieldLabel htmlFor={`${prefix}_doc_type`}>Tipo documento</EpaycoFieldLabel>
            <select
                id={`${prefix}_doc_type`}
                name="doc_type"
                className="form-control form-control-sm"
                value={form.doc_type}
                onChange={onChange}
                disabled={disabled}
            >
                {docTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
        </div>
        <div className="col-md-8 form-group mb-2">
            <EpaycoFieldLabel htmlFor={`${prefix}_doc_number`}>Documento de identificación</EpaycoFieldLabel>
            <input
                id={`${prefix}_doc_number`}
                name="doc_number"
                className="form-control form-control-sm"
                placeholder="Cédula o NIT"
                value={form.doc_number}
                onChange={onChange}
                required
                disabled={disabled}
            />
        </div>
    </div>
);

const EpaycoActivateFormFields = ({ form, onChange, disabled, docTypes }) => (
    <>
        <div className="form-group mb-2">
            <EpaycoFieldLabel htmlFor="activate_name">Nombre del titular</EpaycoFieldLabel>
            <input
                id="activate_name"
                name="name"
                className="form-control form-control-sm"
                placeholder="Nombre completo como en la tarjeta"
                value={form.name}
                onChange={onChange}
                required
                disabled={disabled}
            />
        </div>
        <div className="row">
            <div className="col-md-6 form-group mb-2">
                <EpaycoFieldLabel htmlFor="activate_last_name">Apellido del titular</EpaycoFieldLabel>
                <input
                    id="activate_last_name"
                    name="last_name"
                    className="form-control form-control-sm"
                    placeholder="Apellido"
                    value={form.last_name}
                    onChange={onChange}
                    disabled={disabled}
                />
            </div>
            <div className="col-md-6 form-group mb-2">
                <EpaycoFieldLabel htmlFor="activate_cell_phone">Celular (10 dígitos)</EpaycoFieldLabel>
                <input
                    id="activate_cell_phone"
                    name="cell_phone"
                    type="tel"
                    className="form-control form-control-sm"
                    placeholder="3001234567"
                    value={form.cell_phone}
                    onChange={onChange}
                    required
                    disabled={disabled}
                    maxLength={10}
                    inputMode="numeric"
                    autoComplete="tel"
                />
            </div>
        </div>
        <div className="form-group mb-2">
            <EpaycoFieldLabel htmlFor="activate_email">Correo electrónico</EpaycoFieldLabel>
            <input
                id="activate_email"
                name="email"
                type="email"
                className="form-control form-control-sm"
                placeholder="ejemplo@correo.com"
                value={form.email}
                onChange={onChange}
                required
                disabled={disabled}
            />
        </div>
        <EpaycoPaymentCardFields prefix="activate" form={form} onChange={onChange} disabled={disabled} />
        <EpaycoDocumentFields prefix="activate" form={form} onChange={onChange} disabled={disabled} docTypes={docTypes} />
    </>
);

const EpaycoChangePaymentFormFields = ({ form, onChange, disabled, docTypes }) => (
    <>
        <p className="small text-muted mb-2">Los datos de tarjeta se tokenizan en ePayco; Smile no almacena el número completo.</p>
        <EpaycoDocumentFields prefix="change" form={form} onChange={onChange} disabled={disabled} docTypes={docTypes} />
        <EpaycoPaymentCardFields prefix="change" form={form} onChange={onChange} disabled={disabled} />
    </>
);

const EpaycoDebugCollapsible = ({ payload, testMode }) => {
    if (!payload || !testMode) {
        return null;
    }

    return (
        <details className="sa-epayco-debug-details mt-2">
            <summary className="small font-weight-bold">Debug ePayco (JSON — solo pruebas)</summary>
            <div className="mt-2">
                <button
                    type="button"
                    className="btn btn-sm btn-outline-dark mb-2"
                    onClick={() => {
                        const text = JSON.stringify(payload, null, 2);
                        if (navigator.clipboard?.writeText) {
                            navigator.clipboard.writeText(text);
                        }
                    }}
                >
                    Copiar JSON
                </button>
                <pre className="sa-epayco-debug-pre">{JSON.stringify(payload, null, 2)}</pre>
            </div>
        </details>
    );
};

const ClienteForm = () => {
    const history = useHistory();
    const { id } = useParams();
    const isEditMode = !!id;
    const [formData, setFormData] = useState({
        nombre: '',
        nit: '',
        razon_social: '',
        direccion: '',
        telefono: '',
        observacion: '',
        email_contacto: '',
        limite_usuarios: '',
        usuarios_medicos_disponibles: 2,
        usuarios_administrativos_disponibles: 3,
        estado: 0,
        tipo_contrato: 'mensual',
        fecha_suscripcion: '',
        fecha_vencimiento: '',
        fecha_bloqueo: '',
        fecha_primer_pago: '',
        fecha_renovacion: '',
        almacenamiento_limite_gb: 0,
    });
    const [servicios, setServicios] = useState(defaultServicios());
    const [originalMedicos, setOriginalMedicos] = useState(2);
    const [originalAdmin, setOriginalAdmin] = useState(3);
    const [usuariosMedicosCreados, setUsuariosMedicosCreados] = useState(0);
    const [usuariosAdminCreados, setUsuariosAdminCreados] = useState(0);
    const [usuariosActuales, setUsuariosActuales] = useState(0);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(isEditMode);
    const [cobros, setCobros] = useState([{ descripcion: '', valor: '', tipo: 'base', activo: true }]);
    const [syncEpaycoPlan, setSyncEpaycoPlan] = useState(true);
    const [epaycoMsg, setEpaycoMsg] = useState('');
    const [epaycoPlanId, setEpaycoPlanId] = useState('');
    const [epaycoMontoPlan, setEpaycoMontoPlan] = useState(null);
    const [epaycoSubscription, setEpaycoSubscription] = useState(null);
    const [loadingEpaycoSub, setLoadingEpaycoSub] = useState(false);
    const [showSupportActivate, setShowSupportActivate] = useState(false);
    const [showModalCrearAdmin, setShowModalCrearAdmin] = useState(false);
    const [nuevoClienteId, setNuevoClienteId] = useState(null);
    const [supportActivateForm, setSupportActivateForm] = useState({
        name: '',
        last_name: '',
        email: '',
        doc_type: 'CC',
        doc_number: '',
        cell_phone: '',
        card_number: '',
        card_exp_month: '',
        card_exp_year: '',
        card_cvc: '',
    });
    const [supportActivateMsg, setSupportActivateMsg] = useState('');
    const [supportActivateSubmitting, setSupportActivateSubmitting] = useState(false);
    const [showSupportPaymentMethod, setShowSupportPaymentMethod] = useState(false);
    const [supportPaymentForm, setSupportPaymentForm] = useState({
        doc_type: 'CC',
        doc_number: '',
        card_number: '',
        card_exp_month: '',
        card_exp_year: '',
        card_cvc: '',
    });
    const [supportPaymentMsg, setSupportPaymentMsg] = useState('');
    const [supportPaymentSubmitting, setSupportPaymentSubmitting] = useState(false);
    const [supportCancelling, setSupportCancelling] = useState(false);
    const [supportRetrying, setSupportRetrying] = useState(false);
    const [chargingPayment, setChargingPayment] = useState(false);
    const [chargeMsg, setChargeMsg] = useState('');
    const [chargeMsgType, setChargeMsgType] = useState('success');
    const [chargeDebugPayload, setChargeDebugPayload] = useState(null);

    const DOC_TYPES = ['CC', 'CE', 'NIT', 'PP'];

    useEffect(() => {
        const token = localStorage.getItem('superadmin_token');
        const user = localStorage.getItem('superadmin_user');

        if (!token || !user) {
            history.push('/superadmin/login');
            return;
        }

        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        if (isEditMode) {
            fetchCliente();
        }
    }, [history, id, isEditMode]);

    const fetchCliente = async () => {
        try {
            const response = await axios.get(`/superadmin/clientes/${id}`);
            const cliente = response.data;
            setFormData({
                nombre: cliente.nombre || '',
                nit: cliente.nit || '',
                razon_social: cliente.razon_social || '',
                direccion: cliente.direccion || '',
                telefono: cliente.telefono || '',
                observacion: cliente.observacion || '',
                email_contacto: cliente.email_contacto || '',
                limite_usuarios: cliente.limite_usuarios || '',
                usuarios_medicos_disponibles: cliente.usuarios_medicos_disponibles ?? 2,
                usuarios_administrativos_disponibles: cliente.usuarios_administrativos_disponibles ?? 3,
                estado: cliente.estado != null ? Number(cliente.estado) : 0,
                tipo_contrato: cliente.tipo_contrato || 'mensual',
                fecha_suscripcion: toInputDateValue(cliente.fecha_suscripcion),
                fecha_vencimiento: toInputDateValue(cliente.fecha_vencimiento),
                fecha_bloqueo: toInputDateValue(cliente.fecha_bloqueo),
                fecha_primer_pago: toInputDateValue(cliente.fecha_primer_pago ?? cliente.fecha_primera_carga),
                fecha_renovacion: toInputDateValue(cliente.fecha_renovacion),
                almacenamiento_limite_gb: cliente.almacenamiento_limite_gb ?? 0,
            });
            setOriginalMedicos(cliente.usuarios_medicos_disponibles ?? 2);
            setOriginalAdmin(cliente.usuarios_administrativos_disponibles ?? 3);
            setUsuariosMedicosCreados(cliente.usuarios_medicos_creados ?? 0);
            setUsuariosAdminCreados(cliente.usuarios_administrativos_creados ?? 0);
            setUsuariosActuales(cliente.usuarios_actuales || 0);
            if (Array.isArray(cliente.cobros) && cliente.cobros.length > 0) {
                setCobros(
                    cliente.cobros.map((c) => ({
                        descripcion: c.descripcion || '',
                        valor: c.valor != null ? String(c.valor) : '',
                        tipo: c.tipo || 'base',
                        activo: c.activo === undefined || c.activo === 1 || c.activo === true,
                    }))
                );
            } else {
                setCobros([{ descripcion: '', valor: '', tipo: 'base', activo: true }]);
            }
            setEpaycoPlanId(cliente.epayco_plan_id || '');
            setEpaycoMontoPlan(cliente.epayco_monto_plan != null ? Number(cliente.epayco_monto_plan) : null);
            setSupportActivateForm((prev) => ({
                ...prev,
                name: cliente.nombre || prev.name,
                email: cliente.email_contacto || prev.email,
                cell_phone: normalizeColombianMobileInput(cliente.telefono) || prev.cell_phone,
                doc_number: cliente.nit || prev.doc_number,
                doc_type: cliente.nit ? 'NIT' : prev.doc_type,
            }));

            setServicios(mapServiciosFromApi(cliente.servicios));

            setLoading(false);
            fetchEpaycoSubscription(id);
        } catch (error) {
            console.error('Error fetching cliente:', error);
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('superadmin_token');
                localStorage.removeItem('superadmin_user');
                history.push('/superadmin/login');
            } else {
                setErrors({ general: 'Error al cargar el cliente' });
                setLoading(false);
            }
        }
    };

    const fetchEpaycoSubscription = async (clienteId) => {
        setLoadingEpaycoSub(true);
        try {
            const res = await axios.get(`/superadmin/clientes/${clienteId}/epayco/subscription`);
            setEpaycoSubscription(res.data);
        } catch (error) {
            console.warn('No se pudo cargar estado ePayco:', error);
            setEpaycoSubscription(null);
        } finally {
            setLoadingEpaycoSub(false);
        }
    };

    const handleSupportActivateField = (e) => {
        const { name, value } = e.target;
        setSupportActivateForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSupportPaymentField = (e) => {
        const { name, value } = e.target;
        setSupportPaymentForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSuperadminRetryCharge = async () => {
        if (!isEditMode || !window.confirm('¿Reintentar el pago ePayco con la tarjeta registrada?')) {
            return;
        }
        setSupportRetrying(true);
        setSupportPaymentMsg('');
        try {
            const res = await axios.post(`/superadmin/clientes/${id}/epayco/retry-charge`);
            setChargeMsgType(res.data?.debug?.summary?.charge_status === 'rejected' ? 'warning' : 'success');
            setChargeMsg(res.data?.message || 'Cobro ejecutado.');
            setChargeDebugPayload(res.data?.debug || res.data);
            await fetchCliente();
            await fetchEpaycoSubscription(id);
        } catch (error) {
            setChargeMsgType('danger');
            setChargeMsg(error.response?.data?.message || 'No se pudo reintentar el pago.');
        } finally {
            setSupportRetrying(false);
        }
    };

    const handleSuperadminUpdatePaymentMethod = async () => {
        if (!isEditMode) return;
        setSupportPaymentSubmitting(true);
        setSupportPaymentMsg('');
        try {
            const res = await axios.post(`/superadmin/clientes/${id}/epayco/update-payment-method`, {
                ...supportPaymentForm,
                doc_type: supportPaymentForm.doc_type || supportActivateForm.doc_type,
                doc_number: supportPaymentForm.doc_number || supportActivateForm.doc_number,
                charge_after: true,
            });
            setChargeMsgType(res.data?.debug?.summary?.charge_status === 'rejected' ? 'warning' : 'success');
            setChargeMsg(res.data?.message || 'Método de pago actualizado.');
            setChargeDebugPayload(res.data?.debug || res.data);
            await fetchCliente();
            await fetchEpaycoSubscription(id);
            setShowSupportPaymentMethod(false);
        } catch (error) {
            setSupportPaymentMsg(error.response?.data?.message || 'No se pudo actualizar el método de pago.');
        } finally {
            setSupportPaymentSubmitting(false);
        }
    };

    const handleSuperadminCancelSubscription = async () => {
        if (
            !isEditMode
            || !window.confirm(
                '¿Cancelar DEFINITIVAMENTE la suscripción en ePayco?\n\n'
                + 'El cliente perderá el vínculo local. Si queda bloqueado, deberá contactar soporte para una nueva inscripción.'
            )
        ) {
            return;
        }
        setSupportCancelling(true);
        setSupportPaymentMsg('');
        try {
            const res = await axios.post(`/superadmin/clientes/${id}/epayco/subscription/cancel`);
            setEpaycoMsg(res.data?.message || 'Suscripción cancelada.');
            await fetchEpaycoSubscription(id);
        } catch (error) {
            setSupportPaymentMsg(error.response?.data?.message || 'No se pudo cancelar la suscripción.');
        } finally {
            setSupportCancelling(false);
        }
    };

    const handleSupportActivate = async () => {
        if (!isEditMode) return;

        setSupportActivateSubmitting(true);
        setSupportActivateMsg('');
        try {
            const res = await axios.post(`/superadmin/clientes/${id}/epayco/subscription/activate`, {
                ...supportActivateForm,
                fecha_suscripcion: formData.fecha_suscripcion || null,
            });
            const subId = res.data?.subscription?.subscription_id;
            setSupportActivateMsg(
                subId
                    ? `${res.data?.message || 'Suscripción registrada.'} ID: ${subId}`
                    : (res.data?.message || 'Suscripción registrada.')
            );
            await fetchEpaycoSubscription(id);
            setEpaycoPlanId(res.data?.subscription?.plan_id || epaycoPlanId);
            if (subId) {
                setShowSupportActivate(false);
            }
        } catch (error) {
            const data = error.response?.data;
            setSupportActivateMsg(
                data?.details
                    ? `${data.message}: ${data.details}`
                    : data?.message || 'No se pudo activar la suscripción.'
            );
        } finally {
            setSupportActivateSubmitting(false);
        }
    };

    const handleManualCharge = async () => {
        if (!isEditMode || !window.confirm('¿Ejecutar el débito inicial ePayco hoy? Solo puede hacerse una vez.')) {
            return;
        }

        setChargingPayment(true);
        setChargeMsg('');
        setChargeDebugPayload(null);
        try {
            const res = await axios.post(`/superadmin/clientes/${id}/epayco/charge`, {
                fecha_suscripcion: formData.fecha_suscripcion || null,
            });
            const rejected = res.data?.charge?.charge_status === 'rejected'
                || res.data?.debug?.summary?.charge_status === 'rejected';
            setChargeMsgType(rejected ? 'warning' : 'success');
            setChargeMsg(res.data?.message || (rejected ? 'Débito rechazado.' : 'Cobro ejecutado.'));
            setChargeDebugPayload(res.data?.debug || res.data);
            await fetchCliente();
            await fetchEpaycoSubscription(id);
        } catch (error) {
            const data = error.response?.data;
            setChargeMsgType('danger');
            setChargeMsg(
                data?.details
                    ? `${data.message}: ${data.details}`
                    : data?.message || 'No se pudo ejecutar el cobro.'
            );
            setChargeDebugPayload(data?.debug || data);
        } finally {
            setChargingPayment(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        let parsed = value;
        if (name === 'almacenamiento_limite_gb') {
            if (value === '' || value === null) {
                parsed = '';
            } else {
                const n = parseFloat(value);
                parsed = Number.isFinite(n) ? n : '';
            }
        } else if (type === 'number') {
            parsed = parseInt(value, 10) || '';
        } else if (name === 'estado') {
            parsed = parseInt(value, 10);
        }
        setFormData({
            ...formData,
            [name]: parsed,
        });
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };

    const updateServicio = (key, field, value) => {
        setServicios((prev) => ({
            ...prev,
            [key]: { ...prev[key], [field]: value },
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.nombre.trim()) {
            newErrors.nombre = 'El nombre es requerido';
        }

        if (!formData.nit.trim()) {
            newErrors.nit = 'El NIT es requerido';
        }

        if (!formData.email_contacto.trim()) {
            newErrors.email_contacto = 'El email de contacto es requerido';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_contacto)) {
            newErrors.email_contacto = 'El email no tiene un formato válido';
        }

        const medicos = parseInt(formData.usuarios_medicos_disponibles, 10) || 0;
        const admin = parseInt(formData.usuarios_administrativos_disponibles, 10) || 0;
        const total = medicos + admin;

        if (medicos < 0 || admin < 0) {
            newErrors.usuarios_medicos_disponibles = 'Los cupos no pueden ser negativos';
        }

        if (total < 1) {
            newErrors.usuarios_medicos_disponibles = 'Debe asignar al menos un cupo (médico o administrativo)';
        }

        if (isEditMode && medicos < usuariosMedicosCreados) {
            newErrors.usuarios_medicos_disponibles = `No puede ser menor a los cupos médicos ya asignados (${usuariosMedicosCreados})`;
        }

        if (isEditMode && admin < usuariosAdminCreados) {
            newErrors.usuarios_administrativos_disponibles = `No puede ser menor a los cupos administrativos ya asignados (${usuariosAdminCreados})`;
        }

        if (isEditMode && total < usuariosActuales) {
            newErrors.usuarios_medicos_disponibles = `El total (${total}) no puede ser menor a los usuarios activos (${usuariosActuales})`;
        }

        if (servicios.sms.activo && String(servicios.sms.limite).trim() === '') {
            newErrors.sms_limite = 'Indique el límite de SMS';
        }

        if (servicios.whatsapp.activo && String(servicios.whatsapp.limite).trim() === '') {
            newErrors.wa_limite = 'Indique el límite de WhatsApp';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);

        const buildServiciosPayload = () => {
            const keys = ['fev', 'sms', 'whatsapp', 'consentimientos'];
            const out = {};
            keys.forEach((key) => {
                const s = servicios[key];
                const item = {
                    activo: !!s.activo,
                    periodicidad: s.periodicidad || 'mensual',
                    fecha_inicio: s.fecha_inicio || null,
                };
                if (key !== 'fev') {
                    item.limite = s.limite !== '' && s.limite != null ? parseInt(s.limite, 10) : null;
                }
                out[key] = item;
            });
            return out;
        };

        const payload = {
            ...formData,
            usuarios_medicos_disponibles: parseInt(formData.usuarios_medicos_disponibles, 10) || 0,
            usuarios_administrativos_disponibles: parseInt(formData.usuarios_administrativos_disponibles, 10) || 0,
            limite_usuarios:
                (parseInt(formData.usuarios_medicos_disponibles, 10) || 0) +
                (parseInt(formData.usuarios_administrativos_disponibles, 10) || 0),
            estado: parseInt(formData.estado, 10),
            fecha_suscripcion: formData.fecha_suscripcion || null,
            fecha_vencimiento: formData.fecha_vencimiento || null,
            fecha_bloqueo: formData.fecha_bloqueo || null,
            almacenamiento_limite_gb: (() => {
                const n = parseFloat(formData.almacenamiento_limite_gb);
                return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : 0;
            })(),
            cobros: cobros
                .filter((c) => String(c.descripcion || '').trim() !== '')
                .map((c) => ({
                    descripcion: String(c.descripcion).trim(),
                    valor: parseFloat(c.valor) || 0,
                    tipo: c.tipo || 'otro',
                    activo: !!c.activo,
                })),
            sync_epayco_plan: syncEpaycoPlan,
            servicios: buildServiciosPayload(),
        };
        delete payload.fecha_primer_pago;
        delete payload.fecha_renovacion;

        try {
            if (isEditMode) {
                const res = await axios.put(`/superadmin/clientes/${id}`, payload);
                const sync = res.data?.epayco_sync;
                if (sync?.error) {
                    const detalle = sync.epayco_detail
                        ? ` (${typeof sync.epayco_detail === 'string' ? sync.epayco_detail : JSON.stringify(sync.epayco_detail)})`
                        : '';
                    setEpaycoMsg('Cliente guardado, pero ePayco reportó: ' + sync.error + detalle);
                    setSubmitting(false);
                    return;
                }
                if (sync?.plan_id) {
                    setEpaycoPlanId(sync.plan_id);
                    setEpaycoMontoPlan(sync.amount);
                    setEpaycoMsg(
                        `Plan ePayco ${sync.created ? 'creado' : 'actualizado'}: ${sync.plan_id} ($${Number(sync.amount).toLocaleString('es-CO')})`
                    );
                }
                history.push('/superadmin/clientes');
            } else {
                const res = await axios.post('/superadmin/clientes', payload);
                const sync = res.data?.epayco_sync;
                if (sync?.error) {
                    const detalle = sync.epayco_detail
                        ? ` (${typeof sync.epayco_detail === 'string' ? sync.epayco_detail : JSON.stringify(sync.epayco_detail)})`
                        : '';
                    setEpaycoMsg('Cliente creado, pero ePayco reportó: ' + sync.error + detalle);
                    setSubmitting(false);
                    return;
                }
                const createdId = res.data?.id;
                if (createdId) {
                    setNuevoClienteId(createdId);
                    setShowModalCrearAdmin(true);
                    setSubmitting(false);
                    return;
                }
                history.push('/superadmin/clientes');
            }
        } catch (error) {
            console.error('Error saving cliente:', error);
            setErrors(parseApiErrors(error.response?.data));
            setSubmitting(false);
        }
    };

    const cobrosTotal = cobros.reduce((sum, c) => {
        if (!c.activo) return sum;
        return sum + (parseFloat(c.valor) || 0);
    }, 0);

    const updateCobro = (index, field, value) => {
        setCobros((prev) =>
            prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
        );
    };

    const addCobro = () => {
        setCobros((prev) => [...prev, { descripcion: '', valor: '', tipo: 'otro', activo: true }]);
    };

    const removeCobro = (index) => {
        setCobros((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
    };

    const totalCupos =
        (parseInt(formData.usuarios_medicos_disponibles, 10) || 0) +
        (parseInt(formData.usuarios_administrativos_disponibles, 10) || 0);

    const showCuposWarning = () => {
        if (!isEditMode) return false;
        return (
            (formData.usuarios_medicos_disponibles < originalMedicos ||
                formData.usuarios_administrativos_disponibles < originalAdmin) &&
            totalCupos >= usuariosActuales
        );
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner-border text-info" role="status">
                    <span className="sr-only">Cargando...</span>
                </div>
            </div>
        );
    }

    return (
        <SuperadminLayout>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4>
                    <i className="fas fa-building"></i> {isEditMode ? 'Editar Cliente' : 'Nuevo Cliente'}
                </h4>
                <Link to="/superadmin/clientes" className="btn btn-secondary">
                    <i className="fas fa-arrow-left"></i> Volver
                </Link>
            </div>

            {errors.general && (
                <div className="alert alert-danger">
                    {errors.general}
                </div>
            )}

            {showCuposWarning() && (
                <div className="alert alert-warning">
                    <i className="fas fa-exclamation-triangle"></i> Está reduciendo cupos de usuario.
                    Actualmente hay {usuariosActuales} usuario(s) activo(s) y {usuariosMedicosCreados} médico(s) / {usuariosAdminCreados} administrativo(s) ya parametrizados.
                </div>
            )}

            <form onSubmit={handleSubmit}>
                {/* —— 1. Información básica —— */}
                <div className="form-section">
                    <h5>Información básica</h5>

                    <div className="row">
                        <div className="col-md-5">
                            <div className="form-group">
                                <label htmlFor="nombre">Nombre <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.nombre ? 'is-invalid' : ''}`}
                                    id="nombre"
                                    name="nombre"
                                    value={formData.nombre}
                                    onChange={handleChange}
                                    placeholder="Nombre del cliente"
                                />
                                {errors.nombre && (
                                    <div className="invalid-feedback">{errors.nombre}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-md-2">
                            <div className="form-group">
                                <label htmlFor="nit">NIT/Documento <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.nit ? 'is-invalid' : ''}`}
                                    id="nit"
                                    name="nit"
                                    value={formData.nit}
                                    onChange={handleChange}
                                    placeholder="Documento de Identificación"
                                />
                                {errors.nit && (
                                    <div className="invalid-feedback">{errors.nit}</div>
                                )}
                            </div>
                        </div>


                        <div className="col-md-5">
                        <div className="form-group">
                        <label htmlFor="razon_social">Razón Social</label>
                        <input
                            type="text"
                            className="form-control"
                            id="razon_social"
                            name="razon_social"
                            value={formData.razon_social}
                            onChange={handleChange}
                            placeholder="Razón social del cliente"
                        />
                    </div>
                    </div>



                    </div>

               

                 

                    <div className="row">

                    <div className="col-md-5">
                    <div className="form-group">
                        <label htmlFor="direccion">Dirección</label>
                        <input
                            type="text"
                            className="form-control"
                            id="direccion"
                            name="direccion"
                            value={formData.direccion}
                            onChange={handleChange}
                            placeholder="Dirección física"
                        />
                    </div> 
                    </div>  






                        <div className="col-md-2">
                            <div className="form-group">
                                <label htmlFor="telefono">Teléfono</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="telefono"
                                    name="telefono"
                                    value={formData.telefono}
                                    onChange={handleChange}
                                    placeholder="Teléfono"
                                />
                            </div>
                        </div>

                        <div className="col-md-5">
                            <div className="form-group">
                                <label htmlFor="email_contacto">Email de Contacto <span className="text-danger">*</span></label>
                                <input
                                    type="email"
                                    className={`form-control ${errors.email_contacto ? 'is-invalid' : ''}`}
                                    id="email_contacto"
                                    name="email_contacto"
                                    value={formData.email_contacto}
                                    onChange={handleChange}
                                    placeholder="email@ejemplo.com"
                                />
                                {errors.email_contacto && (
                                    <div className="invalid-feedback">{errors.email_contacto}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-12">
                            <div className="form-group">
                                <label htmlFor="observacion">Observación</label>
                                <textarea
                                    className="form-control"
                                    id="observacion"
                                    name="observacion"
                                    rows={3}
                                    value={formData.observacion}
                                    onChange={handleChange}
                                    placeholder="Notas internas sobre el cliente (opcional)"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* —— 2. Licencias —— */}
                <div className="form-section">
                    <h5>Configuración de licencias</h5>

                    <div className="row">
                        <div className="col-md-2">
                            <div className="form-group">
                                <label htmlFor="usuarios_medicos_disponibles">Cupos Médicos <span className="text-danger">*</span></label>
                                <input
                                    type="number"
                                    className={`form-control ${errors.usuarios_medicos_disponibles ? 'is-invalid' : ''}`}
                                    id="usuarios_medicos_disponibles"
                                    name="usuarios_medicos_disponibles"
                                    value={formData.usuarios_medicos_disponibles}
                                    onChange={handleChange}
                                    min="0"
                                />
                                {errors.usuarios_medicos_disponibles && (
                                    <div className="invalid-feedback">{errors.usuarios_medicos_disponibles}</div>
                                )}
                                {isEditMode && (
                                    <small className="form-text text-muted">
                                        Asignados: {usuariosMedicosCreados}
                                    </small>
                                )}
                            </div>
                        </div>

                        <div className="col-md-2">
                            <div className="form-group">
                                <label htmlFor="usuarios_administrativos_disponibles">Cupos Administrativos <span className="text-danger">*</span></label>
                                <input
                                    type="number"
                                    className={`form-control ${errors.usuarios_administrativos_disponibles ? 'is-invalid' : ''}`}
                                    id="usuarios_administrativos_disponibles"
                                    name="usuarios_administrativos_disponibles"
                                    value={formData.usuarios_administrativos_disponibles}
                                    onChange={handleChange}
                                    min="0"
                                />
                                {errors.usuarios_administrativos_disponibles && (
                                    <div className="invalid-feedback">{errors.usuarios_administrativos_disponibles}</div>
                                )}
                                {isEditMode && (
                                    <small className="form-text text-muted">
                                        Asignados: {usuariosAdminCreados}
                                    </small>
                                )}
                            </div>
                        </div>

                        <div className="col-md-2">
                            <div className="form-group">
                                <label>Total licencias</label>
                                <input
                                    type="number"
                                    className="form-control bg-light"
                                    value={totalCupos}
                                    readOnly
                                />
                                <small className="form-text text-muted">
                                    Suma (PS + Admin)
                                </small>
                                {isEditMode && (
                                    <small className="form-text text-muted d-block">
                                        Personal activo: {usuariosActuales}
                                    </small>
                                )}
                            </div>
                        </div>


                        <div className="col-md-3">
                            <div className="form-group">
                                <label htmlFor="estado">Estado</label>
                                <select
                                    className="form-control"
                                    id="estado"
                                    name="estado"
                                    value={formData.estado}
                                    onChange={handleChange}
                                >
                                    <option value={0}>Activo</option>
                                    <option value={1}>Bloqueado</option>
                                    <option value={2}>Retirado</option>
                                    <option value={3}>Pendiente pago activo</option>
                                </select>
                            </div>
                        </div>


                        <div className="col-md-3">
                            <div className="form-group">
                                <label htmlFor="tipo_contrato">Tipo de contrato (SaaS / cobro)</label>
                                <select
                                    className="form-control"
                                    id="tipo_contrato"
                                    name="tipo_contrato"
                                    value={formData.tipo_contrato}
                                    onChange={handleChange}
                                >
                                    <option value="mensual">Mensual</option>
                                    <option value="trimestral">Trimestral</option>
                                    <option value="semestral">Semestral</option>
                                    <option value="anual">Anual</option>
                                    <option value="diario">Diario (pruebas ePayco)</option>
                                </select>
                                <small className="form-text text-muted">
                                    Periodicidad de cobro. «Diario» acelera pruebas: vence/bloquea en 1–2 días.
                                </small>
                            </div>
                        </div>





                    </div>

                   
                </div>

                {/* —— 3. Servicios (cupos operativos) —— */}
                <div className="form-section">
                    <h5>Configuración de servicios</h5>
                    <p className="text-muted small mb-3">
                        Activa solo lo que aplique. Si no marcas un servicio, no se altera su configuración actual.
                        Cada servicio puede tener periodicidad y fecha de reinicio distintas (ej. FEV anual, SMS mensual).
                        El almacenamiento no participa en ese reset: si el límite en GB es mayor que 0, el módulo queda activo.
                    </p>

                    {/* FEV */}
                    <div className="servicio-bloque mb-3">
                        <div className="form-check mb-2">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="servicio_fev"
                                checked={!!servicios.fev.activo}
                                onChange={(e) => updateServicio('fev', 'activo', e.target.checked)}
                            />
                            <label className="form-check-label font-weight-bold" htmlFor="servicio_fev">
                                Facturación electrónica (FEV)
                            </label>
                        </div>
                        {servicios.fev.activo && (
                            <div className="row pl-3">
                                <div className="col-md-4">
                                    <div className="form-group mb-0">
                                        <label htmlFor="fev_periodicidad">Tipo de contrato (reset)</label>
                                        <SelectPeriodicidadReset
                                            id="fev_periodicidad"
                                            value={servicios.fev.periodicidad}
                                            onChange={(e) => updateServicio('fev', 'periodicidad', e.target.value)}
                                        />
                                        <small className="form-text text-muted">
                                            Cupos por resolución FEV; aquí solo el ciclo de reset.
                                        </small>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <ServicioFechaInicio
                                        id="fev_fecha_inicio"
                                        value={servicios.fev.fecha_inicio}
                                        ultimoReset={servicios.fev.ultimo_reset}
                                        onChange={(e) => updateServicio('fev', 'fecha_inicio', e.target.value)}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Almacenamiento — sección propia, sin toggle */}
                    <div className="servicio-bloque mb-3">
                        <p className="font-weight-bold mb-2">Almacenamiento</p>
                        <div className="row pl-0">
                            <div className="col-md-6">
                                <div className="form-group mb-0">
                                    <label htmlFor="almacenamiento_limite_gb">Límite (GB)</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        id="almacenamiento_limite_gb"
                                        name="almacenamiento_limite_gb"
                                        value={formData.almacenamiento_limite_gb}
                                        onChange={handleChange}
                                        min="0"
                                        step="0.1"
                                    />
                                    <small className="form-text text-muted">
                                        <strong>0</strong> = módulo deshabilitado. Mayor que 0 = activo. Decimales: 0.1 ≈ 100 MB.
                                        No se resetea el consumo; solo cambia el tope.
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SMS */}
                    <div className="servicio-bloque mb-3">
                        <div className="form-check mb-2">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="servicio_sms"
                                checked={!!servicios.sms.activo}
                                onChange={(e) => updateServicio('sms', 'activo', e.target.checked)}
                            />
                            <label className="form-check-label font-weight-bold" htmlFor="servicio_sms">
                                Mensajes de texto (SMS)
                            </label>
                        </div>
                        {servicios.sms.activo && (
                            <div className="row pl-3">
                                <div className="col-md-4">
                                    <div className="form-group">
                                        <label htmlFor="sms_limite">Límite</label>
                                        <input
                                            type="number"
                                            className={`form-control${errors.sms_limite ? ' is-invalid' : ''}`}
                                            id="sms_limite"
                                            min="0"
                                            value={servicios.sms.limite}
                                            onChange={(e) => updateServicio('sms', 'limite', e.target.value)}
                                            placeholder="Ej. 100"
                                        />
                                        {errors.sms_limite && (
                                            <div className="invalid-feedback d-block">{errors.sms_limite}</div>
                                        )}
                                        {servicios.sms.consumo_actual != null && (
                                            <small className="form-text text-muted">
                                                Consumo actual: {servicios.sms.consumo_actual} (no se modifica al guardar).
                                            </small>
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-group">
                                        <label htmlFor="sms_periodicidad">Tipo de contrato (reset)</label>
                                        <SelectPeriodicidadReset
                                            id="sms_periodicidad"
                                            value={servicios.sms.periodicidad}
                                            onChange={(e) => updateServicio('sms', 'periodicidad', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <ServicioFechaInicio
                                        id="sms_fecha_inicio"
                                        value={servicios.sms.fecha_inicio}
                                        ultimoReset={servicios.sms.ultimo_reset}
                                        onChange={(e) => updateServicio('sms', 'fecha_inicio', e.target.value)}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* WhatsApp */}
                    <div className="servicio-bloque mb-3">
                        <div className="form-check mb-2">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="servicio_whatsapp"
                                checked={!!servicios.whatsapp.activo}
                                onChange={(e) => updateServicio('whatsapp', 'activo', e.target.checked)}
                            />
                            <label className="form-check-label font-weight-bold" htmlFor="servicio_whatsapp">
                                Mensajes de WhatsApp
                            </label>
                        </div>
                        {servicios.whatsapp.activo && (
                            <div className="row pl-3">
                                <div className="col-md-4">
                                    <div className="form-group">
                                        <label htmlFor="wa_limite">Límite</label>
                                        <input
                                            type="number"
                                            className={`form-control${errors.wa_limite ? ' is-invalid' : ''}`}
                                            id="wa_limite"
                                            min="0"
                                            value={servicios.whatsapp.limite}
                                            onChange={(e) => updateServicio('whatsapp', 'limite', e.target.value)}
                                            placeholder="Ej. 100"
                                        />
                                        {errors.wa_limite && (
                                            <div className="invalid-feedback d-block">{errors.wa_limite}</div>
                                        )}
                                        {servicios.whatsapp.consumo_actual != null && (
                                            <small className="form-text text-muted">
                                                Consumo actual: {servicios.whatsapp.consumo_actual} (no se modifica al guardar).
                                            </small>
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-group">
                                        <label htmlFor="wa_periodicidad">Tipo de contrato (reset)</label>
                                        <SelectPeriodicidadReset
                                            id="wa_periodicidad"
                                            value={servicios.whatsapp.periodicidad}
                                            onChange={(e) => updateServicio('whatsapp', 'periodicidad', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <ServicioFechaInicio
                                        id="wa_fecha_inicio"
                                        value={servicios.whatsapp.fecha_inicio}
                                        ultimoReset={servicios.whatsapp.ultimo_reset}
                                        onChange={(e) => updateServicio('whatsapp', 'fecha_inicio', e.target.value)}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Consentimientos informados — pendiente backend */}
                    <div className="servicio-bloque mb-3">
                        <div className="form-check mb-2">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="servicio_consentimientos"
                                checked={!!servicios.consentimientos.activo}
                                onChange={(e) => updateServicio('consentimientos', 'activo', e.target.checked)}
                            />
                            <label className="form-check-label font-weight-bold" htmlFor="servicio_consentimientos">
                                Consentimientos informados
                            </label>
                        </div>
                        {servicios.consentimientos.activo && (
                            <div className="row pl-3">
                                <div className="col-md-4">
                                    <div className="form-group">
                                        <label htmlFor="consentimientos_limite">Límite</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id="consentimientos_limite"
                                            min="0"
                                            value={servicios.consentimientos.limite}
                                            onChange={(e) => updateServicio('consentimientos', 'limite', e.target.value)}
                                            placeholder="Ej. 100"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-group">
                                        <label htmlFor="consentimientos_periodicidad">Tipo de contrato (reset)</label>
                                        <SelectPeriodicidadReset
                                            id="consentimientos_periodicidad"
                                            value={servicios.consentimientos.periodicidad}
                                            onChange={(e) =>
                                                updateServicio('consentimientos', 'periodicidad', e.target.value)
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <ServicioFechaInicio
                                        id="consentimientos_fecha_inicio"
                                        value={servicios.consentimientos.fecha_inicio}
                                        ultimoReset={servicios.consentimientos.ultimo_reset}
                                        onChange={(e) => updateServicio('consentimientos', 'fecha_inicio', e.target.value)}
                                    />
                                </div>
                            </div>
                        )}
                        <small className="form-text text-muted pl-3">
                            Módulo operativo en desarrollo; control de reset ya se guarda en{' '}
                            <code>control_servicios_cliente</code>.
                        </small>
                    </div>
                </div>

                {/* —— 4. Cobro —— */}
                <div className="form-section">
                    <h5>Configuración de cobro</h5>
                    <p className="text-muted small mb-3">
                        Fechas del ciclo de dinero (independiente del reset de cupos FEV/SMS/WhatsApp).
                        Cron diario 07:00 (Bogotá): primer débito si la suscripción está inscrita sin primer pago
                        (mensual: día de <code>fecha_suscripcion</code>; diario: fecha ≤ hoy). Renovaciones: ePayco.
                    </p>

                    <div className="row">
                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="fecha_suscripcion">Fecha de suscripción</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    id="fecha_suscripcion"
                                    name="fecha_suscripcion"
                                    value={formData.fecha_suscripcion}
                                    onChange={handleChange}
                                />
                                <small className="form-text text-muted">
                                    {formData.tipo_contrato === 'diario' ? (
                                        <>Contrato diario (pruebas): cualquier día; el débito es hoy y las fechas avanzan 1 día.</>
                                    ) : (
                                        <>Día de cobro: ancla en <code>fecha_suscripcion</code> (inscripción: días <strong>1–10</strong>).</>
                                    )}
                                </small>
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="fecha_vencimiento">Fecha de vencimiento</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    id="fecha_vencimiento"
                                    name="fecha_vencimiento"
                                    value={formData.fecha_vencimiento}
                                    onChange={handleChange}
                                />
                                <small className="form-text text-muted">
                                    Fin del periodo pagado → cron pendiente pago (estado 3).
                                </small>
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="fecha_bloqueo">Fecha de bloqueo</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    id="fecha_bloqueo"
                                    name="fecha_bloqueo"
                                    value={formData.fecha_bloqueo}
                                    onChange={handleChange}
                                />
                                <small className="form-text text-muted">
                                    Corte de acceso → cron bloqueo (estado 1).
                                </small>
                            </div>
                        </div>
                    </div>

                    <div className="row align-items-end">
                        <div className="col-md-4">
                            <div className="form-group mb-md-0">
                                <label htmlFor="fecha_primer_pago">Fecha de primer pago</label>
                                <input
                                    type="date"
                                    className="form-control bg-light"
                                    id="fecha_primer_pago"
                                    name="fecha_primer_pago"
                                    value={formData.fecha_primer_pago}
                                    readOnly
                                    disabled
                                />
                                <small className="form-text text-muted">
                                    Primer débito exitoso (automático al cobrar).
                                </small>
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group mb-md-0">
                                <label htmlFor="fecha_renovacion">Última renovación (pago)</label>
                                <input
                                    type="date"
                                    className="form-control bg-light"
                                    id="fecha_renovacion"
                                    name="fecha_renovacion"
                                    value={formData.fecha_renovacion}
                                    readOnly
                                    disabled
                                />
                                <small className="form-text text-muted">
                                    Fecha del último débito exitoso.
                                </small>
                            </div>
                        </div>

                        {isEditMode && (
                            <div className="col-md-4">
                                <div className="form-group mb-0">
                                    <button
                                        type="button"
                                        className="btn btn-success btn-block"
                                        onClick={handleManualCharge}
                                        disabled={chargingPayment || !epaycoSubscription?.can_manual_charge}
                                        title={epaycoSubscription?.manual_charge_reason || ''}
                                    >
                                        {chargingPayment ? (
                                            <>
                                                <i className="fas fa-spinner fa-spin mr-1"></i> Cobrando…
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-credit-card mr-1"></i> Cargar pago
                                            </>
                                        )}
                                    </button>
                                    <small className="form-text text-muted">
                                        {epaycoSubscription?.can_manual_charge
                                            ? 'Prueba oficina: primer débito (sin restricción de día 1/5/10).'
                                            : (epaycoSubscription?.manual_charge_reason
                                                || 'Requiere suscripción ePayco inscrita sin primer pago.')}
                                    </small>
                                    {chargeMsg && (
                                        <div className={`alert py-1 px-2 small mt-2 mb-0 ${chargeMsgType === 'success' ? 'alert-success' : chargeMsgType === 'warning' ? 'alert-warning' : 'alert-danger'}`}>
                                            {chargeMsg}
                                        </div>
                                    )}
                                    <EpaycoDebugCollapsible payload={chargeDebugPayload} testMode={epaycoSubscription?.test} />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* —— 5. ePayco —— */}
                <div className="form-section">
                    <h5>Servicios de cobro ePayco</h5>
                    <p className="text-muted small mb-3">
                        Desglose de precios para crear/actualizar el plan. No configura cupos operativos (eso va en Configuración de servicios).
                    </p>

                    <div className="card border-info">
                        <div className="card-body">
                            {cobros.map((row, index) => (
                                <div className="row align-items-end mb-2" key={index}>
                                    <div className="col-md-5">
                                        <label className="small mb-0">Descripción</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={row.descripcion}
                                            onChange={(e) => updateCobro(index, 'descripcion', e.target.value)}
                                            placeholder="Ej. Plan 5 usuarios"
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label className="small mb-0">Valor (COP)</label>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            min="0"
                                            step="100"
                                            value={row.valor}
                                            onChange={(e) => updateCobro(index, 'valor', e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label className="small mb-0">Tipo</label>
                                        <select
                                            className="form-control form-control-sm"
                                            value={row.tipo}
                                            onChange={(e) => updateCobro(index, 'tipo', e.target.value)}
                                        >
                                            <option value="base">Base</option>
                                            <option value="whatsapp">WhatsApp</option>
                                            <option value="storage">Almacenamiento</option>
                                            <option value="fev">FEV / Facturas</option>
                                            <option value="otro">Otro</option>
                                        </select>
                                    </div>
                                    <div className="col-md-2">
                                        <div className="form-check mt-3">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={`cobro-activo-${index}`}
                                                checked={!!row.activo}
                                                onChange={(e) => updateCobro(index, 'activo', e.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor={`cobro-activo-${index}`}>
                                                Activo
                                            </label>
                                        </div>
                                    </div>
                                    <div className="col-md-1">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => removeCobro(index)}
                                            title="Quitar"
                                        >
                                            <i className="fas fa-times"></i>
                                        </button>
                                    </div>
                                </div>
                            ))}

                            <button type="button" className="btn btn-sm btn-outline-info" onClick={addCobro}>
                                <i className="fas fa-plus"></i> Agregar servicio
                            </button>

                            <div className="mt-3 p-2 bg-light rounded">
                                <strong>Total a cobrar: </strong>
                                ${cobrosTotal.toLocaleString('es-CO', { minimumFractionDigits: 0 })}
                                {epaycoPlanId && (
                                    <small className="d-block text-muted">
                                        Plan ePayco: <code>{epaycoPlanId}</code>
                                        {epaycoMontoPlan != null && (
                                            <> · monto registrado ${Number(epaycoMontoPlan).toLocaleString('es-CO')}</>
                                        )}
                                    </small>
                                )}
                            </div>

                            <div className="form-check mt-2">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="sync_epayco_plan"
                                    checked={syncEpaycoPlan}
                                    onChange={(e) => setSyncEpaycoPlan(e.target.checked)}
                                    disabled={cobrosTotal <= 0}
                                />
                                <label className="form-check-label" htmlFor="sync_epayco_plan">
                                    Al guardar, crear/actualizar plan en ePayco (modo prueba)
                                </label>
                            </div>

                            {epaycoMsg && (
                                <div className="alert alert-info mt-2 mb-0 py-2 small">{epaycoMsg}</div>
                            )}

                            {epaycoSubscription?.test && (
                                <small className="form-text text-muted d-block mt-2">
                                    Modo pruebas: use «Activar suscripción», «Reintentar pago» o «Cambiar tarjeta y pagar» sin checkout directo.
                                </small>
                            )}

                            {isEditMode && (
                                <div className="mt-3 p-3 border rounded bg-white sa-epayco-panel">
                                    <h6 className="mb-1">
                                        <i className="fas fa-credit-card mr-1" />
                                        Suscripción Smile Web
                                        <small className="text-muted font-weight-normal ml-1">(superadmin)</small>
                                    </h6>
                                    <p className="text-muted small mb-2">
                                        Gestión ePayco del cliente. La clínica inscribe o pausa el débito desde su panel.
                                    </p>

                                    <div className="alert alert-light border small py-2 mb-3">
                                        <strong>Pausa vs cancelación:</strong>{' '}
                                        La clínica puede <em>pausar el débito automático</em> en Smile (conserva la suscripción ePayco).
                                        Desde aquí, «Cancelar suscripción (definitivo)» elimina el vínculo en ePayco; si el cliente queda bloqueado,
                                        deberá contactar soporte para una nueva inscripción.
                                    </div>

                                    {supportPaymentMsg && (
                                        <div className="alert alert-danger py-2 small">{supportPaymentMsg}</div>
                                    )}
                                    {loadingEpaycoSub ? (
                                        <p className="text-muted small mb-0">Cargando estado…</p>
                                    ) : epaycoSubscription ? (
                                        (() => {
                                            const subBadge = getEpaycoSubscriptionBadge(epaycoSubscription);
                                            return (
                                        <ul className="list-unstyled small mb-2">
                                            <li><strong>Plan:</strong> <code>{epaycoSubscription.epayco_plan_id || '—'}</code></li>
                                            <li><strong>Monto ciclo:</strong> ${Number(epaycoSubscription.amount || 0).toLocaleString('es-CO')}</li>
                                            <li>
                                                <strong>Estado suscripción:</strong>{' '}
                                                <span className={`badge ${subBadge.className}`}>
                                                    {subBadge.label}
                                                </span>
                                            </li>
                                            <li>
                                                <strong>Débito automático (Smile):</strong>{' '}
                                                {epaycoSubscription.debito_automatico_pausado ? (
                                                    <span className="badge badge-secondary">Pausado por la clínica</span>
                                                ) : (
                                                    <span className="badge badge-success">Activo</span>
                                                )}
                                            </li>
                                            {epaycoSubscription.epayco_subscription_status && (
                                                <li><strong>Estado ePayco:</strong> <code>{epaycoSubscription.epayco_subscription_status}</code></li>
                                            )}
                                            {epaycoSubscription.epayco_subscription_id && (
                                                <li><strong>ID suscripción:</strong> <code>{epaycoSubscription.epayco_subscription_id}</code></li>
                                            )}
                                            {epaycoSubscription.epayco_customer_id && (
                                                <li><strong>Customer:</strong> <code>{epaycoSubscription.epayco_customer_id}</code></li>
                                            )}
                                            {epaycoSubscription.suscripcion_smile_url && (
                                                <li className="mt-1">
                                                    <strong>Ruta clínica:</strong>{' '}
                                                    <a href={epaycoSubscription.suscripcion_smile_url} target="_blank" rel="noopener noreferrer">
                                                        {epaycoSubscription.suscripcion_smile_url}
                                                    </a>
                                                </li>
                                            )}
                                        </ul>
                                            );
                                        })()
                                    ) : (
                                        <p className="text-muted small mb-2">Sin datos de suscripción.</p>
                                    )}

                                    {epaycoSubscription?.epayco_subscription_id && (
                                        <div className="mb-2">
                                            {epaycoSubscription.can_retry_charge && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-warning mr-1 mb-1"
                                                    onClick={handleSuperadminRetryCharge}
                                                    disabled={supportRetrying || supportPaymentSubmitting || chargingPayment}
                                                >
                                                    {supportRetrying ? 'Procesando…' : 'Reintentar pago'}
                                                </button>
                                            )}
                                            {epaycoSubscription.can_update_payment_method && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary mr-1 mb-1"
                                                    onClick={() => setShowSupportPaymentMethod((v) => !v)}
                                                    disabled={supportPaymentSubmitting || supportRetrying}
                                                >
                                                    {showSupportPaymentMethod ? 'Ocultar formulario' : 'Cambiar tarjeta y pagar'}
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger mb-1"
                                                onClick={handleSuperadminCancelSubscription}
                                                disabled={supportCancelling}
                                                title="Cancelación definitiva en ePayco (no es pausa)"
                                            >
                                                {supportCancelling ? 'Cancelando…' : 'Cancelar suscripción (definitivo)'}
                                            </button>
                                        </div>
                                    )}

                                    {showSupportPaymentMethod && (
                                        <div className="border rounded p-3 mb-2 bg-light sa-epayco-form-card">
                                            <h6 className="small font-weight-bold mb-2">Información de pago</h6>
                                            <EpaycoChangePaymentFormFields
                                                form={supportPaymentForm}
                                                onChange={handleSupportPaymentField}
                                                disabled={supportPaymentSubmitting}
                                                docTypes={DOC_TYPES}
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-primary mt-1"
                                                onClick={handleSuperadminUpdatePaymentMethod}
                                                disabled={supportPaymentSubmitting}
                                            >
                                                {supportPaymentSubmitting ? 'Procesando…' : 'Actualizar tarjeta y pagar'}
                                            </button>
                                        </div>
                                    )}

                                    <EpaycoDebugCollapsible payload={chargeDebugPayload} testMode={epaycoSubscription?.test} />

                                    {!epaycoSubscription?.epayco_subscription_id && (
                                        <>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-info mt-2"
                                                onClick={() => setShowSupportActivate((v) => !v)}
                                            >
                                                {showSupportActivate ? 'Ocultar formulario' : 'Activar suscripción (soporte)'}
                                            </button>
                                            {showSupportActivate && (
                                                <div className="mt-3 p-3 border rounded bg-light sa-epayco-form-card" role="group" aria-label="Activar suscripción Smile Web">
                                                    {supportActivateMsg && (
                                                        <div className={`alert py-2 small ${supportActivateMsg.includes('activada') || supportActivateMsg.includes('registrada') ? 'alert-success' : 'alert-danger'}`}>
                                                            {supportActivateMsg}
                                                        </div>
                                                    )}
                                                    {epaycoSubscription?.test && (
                                                        <p className="small text-muted mb-2">
                                                            Tarjeta prueba: <code>4575623182290326</code> · 12/2028 · CVV 123
                                                        </p>
                                                    )}
                                                    <h6 className="small font-weight-bold mb-2">Información de pago</h6>
                                                    <EpaycoActivateFormFields
                                                        form={supportActivateForm}
                                                        onChange={handleSupportActivateField}
                                                        disabled={supportActivateSubmitting}
                                                        docTypes={DOC_TYPES}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-info mt-1"
                                                        onClick={handleSupportActivate}
                                                        disabled={supportActivateSubmitting}
                                                    >
                                                        {supportActivateSubmitting ? 'Activando…' : 'Suscribir método de pago'}
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="form-group">
                    <button
                        type="submit"
                        className="btn btn-superadmin"
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                Guardando...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-save"></i> {isEditMode ? 'Actualizar Cliente' : 'Crear Cliente'}
                            </>
                        )}
                    </button>
                    <Link to="/superadmin/clientes" className="btn btn-secondary ml-2">
                        Cancelar
                    </Link>
                </div>
            </form>

            <Modal
                show={showModalCrearAdmin}
                onHide={() => {
                    setShowModalCrearAdmin(false);
                    history.push('/superadmin/clientes');
                }}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Cliente creado</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-2">El cliente se registró correctamente.</p>
                    <p className="mb-0">¿Desea crear el usuario administrador?</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setShowModalCrearAdmin(false);
                            history.push('/superadmin/clientes');
                        }}
                    >
                        Ir al listado
                    </Button>
                    <Link
                        to={`/superadmin/clientes/${nuevoClienteId}/usuarios/nuevo`}
                        className="btn btn-primary"
                        onClick={() => setShowModalCrearAdmin(false)}
                    >
                        Crear administrador
                    </Link>
                </Modal.Footer>
            </Modal>
        </SuperadminLayout>
    );
};

export default ClienteForm;
